<template>
	<nav class="tabs tabs-subview" :class="{ 'tabs-mini-gap' : miniGap }">
		<div class="tabs-primary" ref="rowContainer" @drop="drop" @dragover="dragOver" @dragenter="dragOver" @dragleave="dragging = false">
			<sw-tab v-for="tab of tabs" :key="tab.name" :name="tab.name" :icon="tab.icon" :title="tab.title"
					:badge="tab.badge" :active="tab.name === active" :minify="minimized(tab.name)" @select="selectTab(tab.name)"></sw-tab>
			<span v-if="dragging" class="drop-bar" :style="dropBarStyle"></span>
		</div>
	</nav>
</template>

<script setup lang="ts">
import {ref, onMounted, watch, computed, type CSSProperties} from "vue";
import SwTab from "@/ui/subview/SwTab.vue";
import {SubviewTab} from "@t/ui/SubView.ts";
import {observeWidth} from "@/utils/Container.ts";
import {calcHorizontalPosition, getDataTransfer, ParsedType, parseObjectRef} from "@/utils/DragDropUtils.ts";
import {SubviewPosition} from "@/ui/subview/EventTypes.ts";
import {SrcType} from "@t/meta/SourceType.ts";

const props = defineProps<{
	/** Name of the layout */
	layout: string;
	/** Unique ID of the block */
	blockId: number;

	tabs: SubviewTab[]
}>();

const active = defineModel();
// subview-move - a subview was moved to a new place
const emit = defineEmits(['subview-move']);

const rowContainer = ref<HTMLDivElement|null>(null);
const width = observeWidth(rowContainer);
const miniGap = ref<boolean>(false);
const dragging = ref<boolean>(false);
const dropPos = ref<number>(0);
const dropIndex = ref<number>(0);

const dropBarStyle = computed<CSSProperties>(() => {
	const s: CSSProperties = {};
	if (dragging.value) {
		s.left = dropPos.value + "px";
	}
	return s;
});

function selectTab(name:string):void {
	active.value = name;
}

interface ItemWidth {
	max: number;	// maximum width of the item on the screen
	min: number;	// minimum possible width of the item
	minimized: boolean;		// is currently minimized
}

function dumpWidths() {
	let b = "";
	let total = 0;
	for (let [key, value] of widths.value) {
		b += key+"="+JSON.stringify(value)+"\n";
		total += value.max;
	}
	console.log(b);
	console.log("Total: " + total);
	console.log("Total (+gaps): " + (total+(16 * (widths.value.size - 1))));
}

const widths = ref<Map<string, ItemWidth>>(new Map());

function minimized(name:string):boolean {
	if (name) {
		const w = widths.value.get(name);
		if (w) return w.minimized;
	}
	return false;
}

function measureTabsRow(el: HTMLDivElement) {
	const links = el.querySelectorAll("a");
	for (let link of links) {
		// Does the link have a name?
		const n = link.dataset.name;
		if (n) {
			const existingMeasurement = widths.value.get(n);
			if (existingMeasurement && existingMeasurement.minimized) continue;	// Do not measure minimized items
			const w = link.getBoundingClientRect().width;
			const measure:ItemWidth = {
				max: w,
				min: w,
				minimized: false
			}
			const icon = link.querySelector(".fi");	// icon inside the link
			if (icon) {
				measure.min = icon.getBoundingClientRect().width;
			}
			widths.value.set(n, measure);
		}
	}
}


function calcMinimizedFor(width:number, tabs:SubviewTab[]):boolean {
	// calc primary row
	let need:number = -16;	// start with minimal gap (on the right)
	for (let t of tabs) {
		if (t.name) {
			const msr = widths.value.get(t.name);
			if (!msr) console.warn("No measurement for " + t.name);
			need += msr.max + 16;	// plus gap
		} else console.warn("Tab with no name: "+JSON.stringify(t));
	}
	//console.log("Need " + need);
	let reducedTo = need;
	for (let i = tabs.length - 1; i >= 0; i--) {
		const t:SubviewTab = tabs[i];
		const msr = widths.value.get(t.name);
		if (reducedTo > width) {
			msr.minimized = true;
			reducedTo = reducedTo - msr.max + msr.min;
		} else {
			msr.minimized = false;
		}
	}
	return reducedTo > width;	// not enough
}

function calcMinimized(screen:number):void {
	miniGap.value = calcMinimizedFor(screen, props.tabs);
}


function measureTabs() {
	if (rowContainer.value) measureTabsRow(rowContainer.value);
	//dumpWidths();
	calcMinimized(width.value);
}

function dragOver(e:DragEvent) {
	// Find data type we can accept
	let pt:ParsedType = getDataTransfer(e.dataTransfer);
	if (pt) {
		e.preventDefault();	// accepting drop

		e.dataTransfer.dropEffect = "move";
		const pos = calcHorizontalPosition(e, rowContainer.value, ":scope > .tab", { start: 0, end: 16, gap: 16 });
		dropPos.value = Math.max(pos.offset, 0);
		dropIndex.value = pos.index;

		dragging.value = true;
		return;
	}
	dragging.value = false;
}

function drop(e: DragEvent) {
	dragging.value = false;
	e.preventDefault();
	let pt:ParsedType = getDataTransfer(e.dataTransfer);
	if (pt) {
		let oref = parseObjectRef(e.dataTransfer.getData(pt.full));
		if (oref.type === SrcType.SUBVIEW) {
			const subviewId = oref.id;
			emit('subview-move', <SubviewPosition>{ block: props.blockId, layout: props.layout, index: dropIndex.value, name: subviewId });
		}
	}
}

watch(width, (w) => calcMinimized(w));
watch(() => props.tabs, () => {
	//console.log("Measure top menu on change");
	measureTabs();
}, { flush: "post", deep: true });


onMounted(() => {
	measureTabs();
});

</script>

<style>
.drop-bar {
	position:absolute;
	height: calc(100% + 8px);
	width: 2px;
	background-color: var(--selection-bg);
	top: -4px;
	left: -2px;
	z-index: 100000;
	pointer-events: none;
	transition: all 250ms;
}
</style>